import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {
  Container,
  Grid,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { capitalize } from "lodash";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import CustomButton from "../../../components/CustomButton";
import CustomCheckbox from "../../../components/CustomCheckbox";
import CustomModal from "../../../components/CustomModal/CustomModal";
import CustomTable from "../../../components/CustomTable";
import CustomTextField from "../../../components/CustomTextField";
import CustomSelect from "../../../components/Select/Select";
import TopBar from "../../../components/TopBar/TopBar";
import {
  getAllClinicPayment,
  getBillingListByPetId,
  getClinicUsers,
  getCustomerSOAById,
  getH1RecordsWithFilter,
  getPaymentsForReport,
  getRecentTransactionsPayment,
  getReportCollectionData,
} from "../../../redux/reducers/clinicSlice";
import { deletePaymentById } from "../../../redux/reducers/paymentSlice";
import { getClinicPetList } from "../../../redux/reducers/petSlice";
import { rolesToCreateInventory } from "../../../util/arrayList";
import { a4PrintConfig } from "../../../util/commonConfig";
import { paymentSourceTypes } from "../../../util/dropList";
import { tabEnum } from "../../../util/enum";
import { printFromNode, warmPrintFrame } from "../../../util/fastPrint";
import { getRightSaluation } from "../../../util/function";
import { tblGrayBack } from "../../../util/object";
import { baseURL } from "../../../util/server";
import StatementOfAccounts, {
  buildSoaList,
} from "../../CommonScreens/Payments/StatementOfAccounts";
import VetAndUpcomingAppointments from "../../CommonScreens/VetAndUpcomingAppointments/VetAndUpcomingAppointments";
import Discount from "../ComingSoon/cards/Discount";
import PaymentMethod from "../ComingSoon/cards/PaymentMethod";
import ReportTable from "../Reports/ReportTable";
import { nameExpan } from "../Reports/tableHeaders";
import Main from "./Billing/Main"; // Add this import for your App component
import Payments from "./Payment";
import PaymentDashboard from "./PaymentDashboard";
import PointOfSaleNew from "./PointOfSaleNew";
import Returns from "./Returns";

const defObj = {
  clinic: {},
  stocks: [],
  dataSharing: {},
  vets: [],
  paymentMethods: [],
};

const colctHdrs = [
  "invoiceNumber",
  "receiptNo",
  "customerUserName",
  "paymentDate",
  "source",
  "amtPaid",
  "paymentMode",
];
const paymentHdrs = [
  "petName",
  "vetName",
  "totalDue",
  "balanceDue",
  "payType",
  "trDate",
];
const openClosedDropList = [
  { name: "Closed", value: "Closed" },
  { name: "Open", value: "Open" },
];
const checkoutEnum = {
  Open: "N",
  Closed: "Y",
  All: "all",
};

export const PAYMENTS_TAB_PATHS = {
  Dashboard: "dashboard",
  Payments: "payments",
  Returns: "returns",
  PointOfSales: "point-of-sale",
  Reports: "reports",
  Master: "master",
};

export const PAYMENTS_PATH_TO_TAB = {
  dashboard: "Dashboard",
  payments: "Payments",
  returns: "Returns",
  "point-of-sale": "PointOfSales",
  reports: "Reports",
  master: "Master",
};

export const PAYMENTS_SUB_TAB_PATHS = {
  "Payment Method": "payment-method",
  Discount: "discount",
};

export const PAYMENTS_PATH_TO_SUB_TAB = {
  "payment-method": "Payment Method",
  discount: "Discount",
};

export const ClinicPaymentsRedirect = () => {
  const location = useLocation();
  const tabFromState = location.state?.activeTab ?? location.state?.selectedTab;
  const path = PAYMENTS_TAB_PATHS[tabFromState] ?? PAYMENTS_TAB_PATHS.Dashboard;
  return <Navigate to={`/payments/${path}`} replace state={location.state} />;
};

const tblHdrs = [
  "date",
  "invoiceNumber",
  "tradeName",
  "quantity",
  "batchNo",
  "expiryDate",
  // "userName",
  "customerName",
  "petNameOnly",
  "doctorName",
  "userAddress",
];

const getYearOptions = () => {
  const currentYear = moment().year();
  return Array.from({ length: 10 }, (_, i) => {
    const year = currentYear - i;
    return { name: String(year), value: year };
  });
};

const getMonthOptions = (selectedYear) => {
  const currentYear = moment().year();
  const currentMonth = moment().month() + 1;
  const maxMonth = selectedYear === currentYear ? currentMonth - 1 : 12;
  if (maxMonth < 1) return [];
  return moment
    ?.months()
    ?.slice(0, maxMonth)
    ?.map((name, index) => ({
      name,
      value: index + 1,
    }));
};

const getDefaultPeriod = () => {
  const now = moment();
  const currentYear = now.year();
  const currentMonth = now.month() + 1;
  if (currentMonth === 1) {
    return { year: currentYear - 1, month: 12 };
  }
  return { year: currentYear, month: currentMonth - 1 };
};

const DATE_RANGE_REPORT_TYPES = [
  "accounts-receivables",
  "consolidated-financial-summary",
];

const initSoaReportValues = {
  visible: false,
  searchType: "pet",
  searchValue: "",
  searchEnabled: false,
  pet: null,
  user: null,
  startDate: new Date(),
  endDate: new Date(),
  dateSelected: false,
};

const isDateRangeReport = (type) => DATE_RANGE_REPORT_TYPES.includes(type);

const getReportModalHeader = (type) => {
  switch (type) {
    case "gst":
      return "GST Report";
    case "hsn":
      return "HSN Summary";
    case "accounts-receivables":
      return "Accounts Receivables Report";
    case "consolidated-financial-summary":
      return "Consolidated Financial Summary";
    default:
      return "Report";
  }
};

const formatReportDateRange = (start, end) => {
  const date1 = moment(new Date(start));
  const date2 = moment(new Date(end));
  return {
    startDate: (date1.isBefore(date2) ? date1 : date2).format("YYYY-MM-DD"),
    endDate: (date1.isBefore(date2) ? date2 : date1).format("YYYY-MM-DD"),
  };
};

const QUARTER_RANGES = [
  { label: "Jan-Mar", startMonth: 0, endMonth: 2 },
  { label: "Apr-Jun", startMonth: 3, endMonth: 5 },
  { label: "Jul-Sept", startMonth: 6, endMonth: 8 },
  { label: "Oct-Dec", startMonth: 9, endMonth: 11 },
];

const getLastFourQuarters = () => {
  const now = moment();
  let year = now.year();
  let quarterIndex = Math.floor(now.month() / 3);
  const quarters = [];

  for (let i = 0; i < 4; i += 1) {
    const quarter = QUARTER_RANGES[quarterIndex];
    quarters.push({
      name: `${quarter.label} ${year}`,
      startDate: moment({ year, month: quarter.startMonth, day: 1 }).format(
        "YYYY-MM-DD",
      ),
      endDate: moment({ year, month: quarter.endMonth, day: 1 })
        .endOf("month")
        .format("YYYY-MM-DD"),
    });
    quarterIndex -= 1;
    if (quarterIndex < 0) {
      quarterIndex = 3;
      year -= 1;
    }
  }

  return quarters;
};

const initBillsReportValues = {
  visible: false,
  period: "day",
  startDate: new Date(),
  endDate: new Date(),
  source: [],
  billType: "Closed",
};

const getBillsReportDateRange = (period, startDate, endDate) => {
  const today = moment();
  if (period === "week") {
    return {
      startDate: today.clone().subtract(6, "days").format("YYYY-MM-DD"),
      endDate: today.format("YYYY-MM-DD"),
    };
  }
  if (period === "month") {
    return {
      startDate: today.clone().subtract(29, "days").format("YYYY-MM-DD"),
      endDate: today.format("YYYY-MM-DD"),
    };
  }
  if (period === "custom") {
    return formatReportDateRange(startDate, endDate);
  }
  return {
    startDate: today.format("YYYY-MM-DD"),
    endDate: today.format("YYYY-MM-DD"),
  };
};

const ClinicPayments = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { tab, subTab: subTabParam } = useParams();
  const activeTab = PAYMENTS_PATH_TO_TAB[tab];
  const masterSubTab =
    PAYMENTS_PATH_TO_SUB_TAB[subTabParam] ?? "Payment Method";
  const [posPayId, setPosPayId] = useState(null);
  const [paymentTableType, setPaymentTableType] = useState(null);
  const [selectedPaymentTab, setSelectedPaymentTab] = useState("Pending");
  const [selectedPayment, setSelecetdPayment] = useState(null);
  const [paymentId, setPaymentId] = useState(null);
  const [delModVsble, setDelModVsble] = useState(false);
  const [masterAnchorEl, setMasterAnchorEl] = useState(null);
  const [reportAnchorEl, setReportAnchorEl] = useState(null);
  const [h1ReportModValues, setH1ReportModValues] = useState({
    visible: false,
  });
  const [reportModalValues, setReportModalValues] = useState({
    visible: false,
  });
  const [ndpsReportValues, setNdpsReportValues] = useState({
    visible: false,
    quarter: null,
  });
  const [dayBookReportValues, setDayBookReportValues] = useState({
    visible: false,
    date: null,
    source: [],
    paymentMode: [],
    type: "Closed",
    valueEntered: false,
    activeTab: "Bills",
  });
  const [billsReportValues, setBillsReportValues] = useState(
    initBillsReportValues,
  );
  const [soaReportValues, setSoaReportValues] = useState(initSoaReportValues);
  const soaPrintRef = useRef(null);
  const [page, setPage] = useState(1);
  const [posModVsble, setPosModVsble] = useState(false);
  const {
    details: clinicDetail,
    h1Records,
    users,
    petBillingList,
    customerSOAList,
    reportPayments,
    reportCollections,
  } = useSelector((state) => state?.clinic);
  const { clinicPetList } = useSelector((state) => state?.pet);
  const itemSize = 10;
  const defaultUrl = "?filter=all&type=resent";
  const mandateDataRaw = sessionStorage.getItem("mandateData");
  const {
    clinic: clinicData,
    stocks,
    dataSharing,
    vets: clinicVets,
    paymentMethods,
  } = useMemo(() => {
    if (!mandateDataRaw) return defObj;
    try {
      const reqData = JSON.parse(mandateDataRaw);
      return {
        clinic: reqData?.clinic ?? {},
        stocks: Array.isArray(reqData?.stocks) ? reqData?.stocks : [],
        dataSharing: reqData?.dataSharing ?? {},
        vets: Array.isArray(reqData?.vets) ? reqData?.vets : [],
        paymentMethods: Array.isArray(reqData?.paymentMethods)
          ? reqData?.paymentMethods
          : [],
      };
    } catch {
      return defObj;
    }
  }, [mandateDataRaw]);
  const clinicDet = clinicData?.id ? clinicData : clinicDetail;
  const allRoles = JSON.parse(localStorage.getItem("allRoles"));
  const role = localStorage.getItem("role");
  const hasInventoryCreateRole = (
    Array.isArray(allRoles) && allRoles.length > 0
      ? allRoles
      : [role].filter(Boolean)
  ).some((r) => rolesToCreateInventory.includes(r));

  const navigateToTab = (nextTab, nextSubTab = null) => {
    const path = PAYMENTS_TAB_PATHS[nextTab];
    if (!path) return;
    const subPath = nextSubTab ? PAYMENTS_SUB_TAB_PATHS[nextSubTab] : null;
    navigate(subPath ? `/payments/${path}/${subPath}` : `/payments/${path}`, {
      state: location.state,
    });
  };

  // Child components call setActiveTab("Payments") etc. — route instead of local state.
  const setActiveTab = (nextTab) => {
    if (!nextTab || nextTab === activeTab) return;
    if (nextTab !== "PointOfSales") {
      setSelecetdPayment(null);
      setPaymentId(null);
    }
    navigateToTab(nextTab);
  };

  const onH1ApiCall = (pge = 1) => {
    setPage(pge);
    const stockIds = h1ReportModValues?.stockName?.map((li) => li?.value);
    const date1 = moment(new Date(h1ReportModValues?.startDate));
    const date2 = moment(new Date(h1ReportModValues?.endDate));
    const startDate = (date1.isBefore(date2) ? date1 : date2).format(
      "YYYY-MM-DD",
    );
    const endDate = (date1.isBefore(date2) ? date2 : date1).format(
      "YYYY-MM-DD",
    );
    const url = `?clinicId=${clinicDet?.id}&stockIds=${stockIds}&startDate=${startDate}&endDate=${endDate}&page=${pge}&itemSize=${itemSize}`;
    dispatch(getH1RecordsWithFilter(url));
  };

  const handleEdit = (dta) => {
    setSelecetdPayment(dta);
    setPaymentId(dta?.paymentId);
    navigateToTab("PointOfSales");
  };

  const handleChangeTab = (tabName) => {
    if (!tabName || tabName === activeTab) return;
    setSelecetdPayment(null);
    setPaymentId(null);
    navigateToTab(tabName);
  };

  const handleDelete = (pay) => {
    setPaymentId(pay?.paymentId);
    setDelModVsble(true);
  };

  const handleCloseDelMod = () => {
    setPaymentId(null);
    setDelModVsble(false);
  };

  const handleConfirmDelete = () => {
    dispatch(deletePaymentById(paymentId)).then((res) => {
      if (res?.payload) {
        handleCloseDelMod();
        dispatch(getRecentTransactionsPayment(defaultUrl));
        dispatch(
          getAllClinicPayment(`?filter=all&type=all&page=1&itemSize=10`),
        );
      }
    });
  };

  const handleChangeMaster = (val) => {
    setMasterAnchorEl(null);
    navigateToTab("Master", val);
  };

  const handleChangeReport = (val) => {
    const stockIds = h1ReportModValues?.stockName?.map((li) => li?.value);
    const date1 = moment(new Date(h1ReportModValues?.startDate));
    const date2 = moment(new Date(h1ReportModValues?.endDate));
    const startDate = (date1.isBefore(date2) ? date1 : date2).format(
      "YYYY-MM-DD",
    );
    const endDate = (date1.isBefore(date2) ? date2 : date1).format(
      "YYYY-MM-DD",
    );
    const url = `${baseURL}/clinic/payment/schedule-h1/export?clinicId=${clinicDet?.id}&stockIds=${stockIds}&startDate=${startDate}&endDate=${endDate}`;
    window.open(url, "_blank");
  };

  const onOpenH1Report = () => {
    setReportAnchorEl(null);
    setH1ReportModValues({
      visible: true,
      stockName: getH1Stocks(),
      // stockName: [],
      startDate: new Date(),
      endDate: new Date(),
    });
  };

  const onOpenPeriodReport = (type) => {
    setReportAnchorEl(null);
    const { year, month } = getDefaultPeriod();
    setReportModalValues({
      visible: true,
      type,
      year,
      month,
    });
  };

  const onOpenDateRangeReport = (type) => {
    setReportAnchorEl(null);
    setReportModalValues({
      visible: true,
      type,
      startDate: new Date(),
      endDate: new Date(),
    });
  };

  const onOpenNdpsReport = () => {
    setReportAnchorEl(null);
    const quarters = getLastFourQuarters();
    setNdpsReportValues({
      visible: true,
      quarter: quarters[0]?.name ?? null,
    });
  };

  const onOpenDayBookReport = () => {
    setReportAnchorEl(null);
    setDayBookReportValues({
      visible: true,
      date: new Date(),
      source: [],
      paymentMode: [],
      type: "Closed",
      valueEntered: false,
      activeTab: "Bills",
    });
  };

  const onOpenBillsReport = () => {
    setReportAnchorEl(null);
    setBillsReportValues({
      ...initBillsReportValues,
      visible: true,
      startDate: new Date(),
      endDate: new Date(),
      source: [],
      billType: "Closed",
    });
  };

  const onCloseBillsReportModal = () => {
    setBillsReportValues(initBillsReportValues);
  };

  const onChangeBillsReportModal = (name, value) => {
    const reqObj = { ...billsReportValues, [name]: value };
    if (name === "period") {
      reqObj.startDateErr = "";
      reqObj.endDateErr = "";
    }
    if (name === "startDate") {
      reqObj.startDateErr = value ? "" : "This field is required";
    }
    if (name === "endDate") {
      reqObj.endDateErr = value ? "" : "This field is required";
    }
    setBillsReportValues(reqObj);
  };

  const onDownloadBillsReport = () => {
    if (billsReportValues?.period === "custom") {
      const startDateErr = billsReportValues?.startDate
        ? ""
        : "This field is required";
      const endDateErr = billsReportValues?.endDate
        ? ""
        : "This field is required";
      if (startDateErr || endDateErr) {
        return setBillsReportValues({
          ...billsReportValues,
          startDateErr,
          endDateErr,
        });
      }
    }

    const { startDate, endDate } = getBillsReportDateRange(
      billsReportValues?.period,
      billsReportValues?.startDate,
      billsReportValues?.endDate,
    );

    // Basic integration based on Payment export-excel; adjust params as needed.
    let url = `${baseURL}/payments/search/export-unpaid-excel?clinicId=${clinicDet?.id}&startDate=${startDate}&endDate=${endDate}&checkOut=${checkoutEnum?.[billsReportValues?.billType]}`;
    // let url = `${baseURL}/payments/search/export-excel?clinicId=${clinicDet?.id}&startDate=${startDate}&endDate=${endDate}&checkOut=${billsReportValues?.billType === "Closed" ? "Y" : "N"}`;
    if (billsReportValues?.source?.length > 0) {
      url += `&type=${
        (billsReportValues?.source ?? [])?.some(
          (li) => li?.value?.toLowerCase() === "all",
        )
          ? ["Clinic", "Pharmacy", "Pet Shop"]
          : (billsReportValues?.source ?? [])?.map((li) => li?.value)
      }`;
    }
    const checkOut = checkoutEnum?.[billsReportValues?.billType];
    const statusParam =
      checkOut === "all"
        ? "unpaid,paid,partiallypaid"
        : checkOut === "Y"
          ? "unpaid,partiallypaid"
          : "unpaid";
    url += `&status=${statusParam}`;
    window.open(url, "_blank");
    onCloseBillsReportModal();
  };

  const onCloseNdpsReportModal = () => {
    setNdpsReportValues({ visible: false, quarter: null });
  };

  const onChangeNdpsReportModal = (name, value) => {
    const reqObj = { ...ndpsReportValues, [name]: value };
    if (name === "quarter") {
      reqObj.quarterErr = value ? "" : "This field is required";
    }
    setNdpsReportValues(reqObj);
  };

  const onDownloadNdpsReport = () => {
    const quarterErr = ndpsReportValues?.quarter
      ? ""
      : "This field is required";
    if (quarterErr) {
      return setNdpsReportValues({ ...ndpsReportValues, quarterErr });
    }
    const selectedQuarter = getLastFourQuarters().find(
      (q) => q?.name === ndpsReportValues?.quarter,
    );
    if (!selectedQuarter) return;
    const url = `${baseURL}/clinic/stock/export/ndps-fda-quarterly?clinicId=${clinicDet?.id}&startDate=${selectedQuarter?.startDate}&endDate=${selectedQuarter?.endDate}`;
    window.open(url, "_blank");
    onCloseNdpsReportModal();
  };

  const onCloseDayBookReportModal = () => {
    setDayBookReportValues({
      visible: false,
      date: null,
      source: [],
      paymentMode: [],
      type: "Closed",
      valueEntered: false,
      activeTab: "Bills",
    });
  };

  const getDayBookSources = (values = dayBookReportValues) => {
    if (!(values?.source?.length > 0)) return [];
    return Array.isArray(values?.source)
      ? values?.source?.some((li) => li?.value?.toLowerCase() === "all")
        ? ["Clinic", "PETSHOP", "Pharmacy"]
        : (values?.source ?? [])?.map((li) => li?.apiValue)?.filter(Boolean)
      : [];
  };

  const getDayBookCheckOut = (type = dayBookReportValues?.type) => {
    return type === "Open" ? ["N"] : ["Y"];
  };

  const getDayBookPaymentModeValues = (values = dayBookReportValues) => {
    if (!(values?.paymentMode?.length > 0)) return [];
    return Array.isArray(values?.paymentMode)
      ? values?.paymentMode?.map((li) => li?.value)?.filter(Boolean)
      : [];
  };

  const fetchDayBookBills = (values = dayBookReportValues) => {
    if (!values?.date) return;
    const formattedDate = moment(new Date(values?.date)).format("YYYY-MM-DD");
    dispatch(
      getPaymentsForReport({
        startDate: formattedDate,
        endDate: formattedDate,
        type: getDayBookSources(values),
        checkOut: getDayBookCheckOut(values?.type),
        summary: true,
      }),
    );
  };

  const fetchDayBookCollections = (values = dayBookReportValues) => {
    if (!values?.date) return;
    const formattedDate = moment(new Date(values?.date)).format("YYYY-MM-DD");
    dispatch(
      getReportCollectionData({
        startPaymentDate: formattedDate,
        endPaymentDate: formattedDate,
        source: getDayBookSources(values),
        paymentMode: getDayBookPaymentModeValues(values),
        summary: true,
      }),
    );
  };

  const onChangeDayBookReportModal = (name, value) => {
    const reqObj = { ...dayBookReportValues, [name]: value };
    if (name === "date") {
      reqObj.dateErr = value ? "" : "This field is required";
    }
    setDayBookReportValues(reqObj);

    if (!reqObj?.valueEntered || !reqObj?.date) return;

    if (
      name === "type" ||
      (name === "source" && reqObj?.activeTab === "Bills")
    ) {
      fetchDayBookBills(reqObj);
    }
    if (
      name === "paymentMode" ||
      (name === "source" && reqObj?.activeTab === "Collections")
    ) {
      fetchDayBookCollections(reqObj);
    }
  };

  const onGenerateDayBookReport = () => {
    const dateErr = dayBookReportValues?.date ? "" : "This field is required";
    if (dateErr) {
      return setDayBookReportValues({ ...dayBookReportValues, dateErr });
    }
    const nextValues = {
      ...dayBookReportValues,
      valueEntered: true,
      type: dayBookReportValues?.type || "Closed",
      paymentMode: dayBookReportValues?.paymentMode ?? [],
    };
    fetchDayBookBills(nextValues);
    fetchDayBookCollections(nextValues);
    setDayBookReportValues(nextValues);
  };

  const onDownloadDayBookReport = () => {
    const dateErr = dayBookReportValues?.date ? "" : "This field is required";
    if (dateErr) {
      return setDayBookReportValues({ ...dayBookReportValues, dateErr });
    }
    const formattedDate = moment(new Date(dayBookReportValues.date)).format(
      "YYYY-MM-DD",
    );
    let url = `${baseURL}/clinic/payment/day-book/export?clinicId=${clinicDet?.id}&startDate=${formattedDate}&endDate=${formattedDate}`;
    if (dayBookReportValues?.source?.length > 0) {
      const types = Array.isArray(dayBookReportValues?.source)
        ? dayBookReportValues?.source?.some(
            (li) => li?.value?.toLowerCase() === "all",
          )
          ? ["clinic", "pharmacy", "petshop"]
          : (dayBookReportValues?.source ?? [])
              ?.map((li) => li?.reportValue)
              ?.filter(Boolean)
        : [];
      types.forEach((type) => {
        url += `&type=${encodeURIComponent(type)}`;
      });
    }
    getDayBookCheckOut(dayBookReportValues?.type).forEach((checkOut) => {
      url += `&checkOut=${encodeURIComponent(checkOut)}`;
    });
    getDayBookPaymentModeValues(dayBookReportValues).forEach((mode) => {
      url += `&paymentMode=${encodeURIComponent(mode)}`;
    });
    window.open(url, "_blank");
    onCloseDayBookReportModal();
  };

  const onOpenSoaReport = () => {
    setReportAnchorEl(null);
    setSoaReportValues({
      ...initSoaReportValues,
      visible: true,
      startDate: new Date(),
      endDate: new Date(),
    });
  };

  const onCloseSoaReportModal = () => {
    setSoaReportValues(initSoaReportValues);
  };

  const onChangeSoaReport = (name, value) => {
    const reqObj = { ...soaReportValues, [name]: value };
    if (name === "searchType") {
      reqObj.searchValue = "";
      reqObj.searchEnabled = false;
      reqObj.pet = null;
      reqObj.user = null;
      reqObj.selectionErr = "";
      reqObj.dateSelected = false;
    }
    if (name === "pet" || name === "user") {
      reqObj.selectionErr = "";
      reqObj.searchEnabled = false;
      reqObj.searchValue = "";
      if (name === "pet") reqObj.user = null;
      if (name === "user") reqObj.pet = null;
    }
    if (name === "searchValue") {
      if (value?.length > 2) {
        if (soaReportValues?.searchType === "user") {
          dispatch(
            getClinicUsers(
              `?search=${value}&itemSize=30&page=1&userData=${dataSharing?.petData ?? false}`,
            ),
          );
        } else {
          dispatch(
            getClinicPetList({
              type: "all",
              page: 1,
              itemSize: 30,
              petName: value,
              petData: dataSharing?.petData,
            }),
          );
        }
      }
      reqObj.searchEnabled = true;
    }
    if (name === "startDate") {
      reqObj.startDateErr = value ? "" : "This field is required";
    }
    if (name === "endDate") {
      reqObj.endDateErr = value ? "" : "This field is required";
    }
    setSoaReportValues(reqObj);
  };

  const soaUserSearchResults = () => {
    if (!soaReportValues?.searchValue) return [];
    const search = soaReportValues.searchValue.toLowerCase();
    return (users ?? [])?.filter(
      (usr) =>
        usr?.name?.toLowerCase().includes(search) ||
        usr?.email?.toLowerCase().includes(search) ||
        usr?.mobile?.toLowerCase().includes(search),
    );
  };

  const soaList = useMemo(() => {
    // if (soaReportValues?.searchType === "user") {
    //   return buildCustomerSoaList(customerSOAList);
    // }
    return buildSoaList(
      (soaReportValues?.searchType === "user"
        ? customerSOAList?.data
        : petBillingList?.data) ?? [],
    );
  }, [soaReportValues?.searchType, customerSOAList, petBillingList?.data]);

  useEffect(() => {
    if (
      !soaReportValues?.visible ||
      !soaReportValues?.dateSelected ||
      typeof window === "undefined"
    )
      return;
    let cancelled = false;
    const warm = () => {
      if (cancelled) return;
      const node = soaPrintRef?.current;
      if (node) warmPrintFrame(node, a4PrintConfig);
    };
    const frameId = requestAnimationFrame(warm);
    const node = soaPrintRef?.current;
    const imgs = node ? [...node.querySelectorAll("img")] : [];
    imgs.forEach((img) => {
      if (!img.complete) {
        img.addEventListener("load", warm, { once: true });
        img.addEventListener("error", warm, { once: true });
      }
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(frameId);
      imgs.forEach((img) => {
        img.removeEventListener("load", warm);
        img.removeEventListener("error", warm);
      });
    };
  }, [soaReportValues?.visible, soaReportValues?.dateSelected, soaList]);

  const handleSoaPrint = () => {
    printFromNode(soaPrintRef?.current, {
      title: `SOA_${Date.now()}`,
      pageStyle: a4PrintConfig,
    });
  };

  const onProceedSoaReport = () => {
    const isPet = soaReportValues?.searchType === "pet";
    const hasSelection = isPet
      ? !!soaReportValues?.pet?.petId
      : !!soaReportValues?.user?.id;
    const startDateErr = soaReportValues?.startDate
      ? ""
      : "This field is required";
    const endDateErr = soaReportValues?.endDate ? "" : "This field is required";
    if (!hasSelection || startDateErr || endDateErr) {
      return setSoaReportValues({
        ...soaReportValues,
        selectionErr: hasSelection
          ? ""
          : `Please select a ${isPet ? "Pet" : "Customer"}`,
        startDateErr,
        endDateErr,
      });
    }

    const startDate = moment(new Date(soaReportValues?.startDate)).format(
      "YYYY-MM-DD",
    );
    const endDate = moment(new Date(soaReportValues?.endDate)).format(
      "YYYY-MM-DD",
    );

    if (isPet) {
      dispatch(
        getBillingListByPetId({
          petId: soaReportValues?.pet?.petId,
          data: {
            page: 1,
            itemSize: 1000,
            startDate,
            endDate,
          },
        }),
      );
      setSoaReportValues({ ...soaReportValues, dateSelected: true });
      return;
    }

    dispatch(
      getCustomerSOAById({
        userId: soaReportValues?.user?.id,
        url: `?startDate=${startDate}&endDate=${endDate}`,
      }),
    ).then((res) => {
      if (res?.payload) {
        setSoaReportValues({ ...soaReportValues, dateSelected: true });
      }
    });
  };

  const onCloseReportModal = () => {
    setReportModalValues({ visible: false });
  };

  const onChangeReportModal = (name, value) => {
    const reqObj = { ...reportModalValues, [name]: value };
    if (name === "year") {
      const monthOptions = getMonthOptions(value);
      if (!monthOptions.some((li) => li?.value === reqObj?.month)) {
        reqObj.month = monthOptions[monthOptions.length - 1]?.value ?? null;
      }
      reqObj.yearErr = value ? "" : "This field is required";
    }
    if (name === "month") {
      reqObj.monthErr = value ? "" : "This field is required";
    }
    if (name === "startDate") {
      reqObj.startDateErr = value ? "" : "This field is required";
    }
    if (name === "endDate") {
      reqObj.endDateErr = value ? "" : "This field is required";
    }
    setReportModalValues(reqObj);
  };

  const onDownloadPeriodReport = () => {
    if (isDateRangeReport(reportModalValues?.type)) {
      const startDateErr = reportModalValues?.startDate
        ? ""
        : "This field is required";
      const endDateErr = reportModalValues?.endDate
        ? ""
        : "This field is required";
      if (startDateErr || endDateErr) {
        return setReportModalValues({
          ...reportModalValues,
          startDateErr,
          endDateErr,
        });
      }
      const { startDate, endDate } = formatReportDateRange(
        reportModalValues?.startDate,
        reportModalValues?.endDate,
      );
      const endpoint =
        reportModalValues?.type === "accounts-receivables"
          ? "/payments/search/export-unpaid-excel" // "/payments/search/export-outstanding-excel"
          : "/clinic/payment/consolidated-financial-summary/export";
      let url = `${baseURL}${endpoint}?clinicId=${clinicDet?.id}&startDate=${startDate}&endDate=${endDate}`;
      if (reportModalValues?.type === "accounts-receivables") {
        url += `&checkOut=Y&type=Clinic,Pharmacy,Pet Shop&status=unpaid`;
      }
      window.open(url, "_blank");
      onCloseReportModal();
      return;
    }
    const yearErr = reportModalValues?.year ? "" : "This field is required";
    const monthErr = reportModalValues?.month ? "" : "This field is required";
    if (yearErr || monthErr) {
      return setReportModalValues({ ...reportModalValues, yearErr, monthErr });
    }
    const endpoint =
      reportModalValues?.type === "gst"
        ? "gst-report/invoices/export"
        : "gst-report/export";
    const url = `${baseURL}/clinic/payment/${endpoint}?clinicId=${clinicDet?.id}&year=${reportModalValues?.year}&month=${reportModalValues?.month}`;
    window.open(url, "_blank");
    onCloseReportModal();
  };

  const onCloseH1Mod = () => {
    setH1ReportModValues({ visible: false });
  };

  const onChange = (name, value) => {
    const reqObj = { ...h1ReportModValues, [name]: value };
    if (name === "stockName") {
      reqObj.stockNameErr = Array.isArray(value)
        ? value?.length > 0
          ? ""
          : "This field is required"
        : "This field is required";
    }
    setH1ReportModValues(reqObj);
  };

  const onGenerate = () => {
    if (
      h1ReportModValues?.stockName?.length === 0 ||
      !h1ReportModValues?.stockName
    ) {
      return setH1ReportModValues({
        ...h1ReportModValues,
        stockNameErr: "This field is required",
      });
    }
    onH1ApiCall(1);
    setH1ReportModValues({ ...h1ReportModValues, valueEntered: true });
  };

  const handleChangePage = (e, pge) => {
    onH1ApiCall(pge);
  };

  const getTableData = () => {
    return (h1Records?.data ?? [])?.map((li) => ({
      ...li,
      petNameOnly: li?.petName,
      customerName: li?.userName,
      date:
        li?.createDate?.length > 0
          ? moment(li?.createDate).format("DD MMM YYYY")
          : "",
      invoiceNumber: li?.invoiceNo ?? "",
      tradeName: li?.stockTradeName ?? "",
      expiryDate:
        li?.expiryDate?.length > 0
          ? moment(new Date(li?.expiryDate)).format("DD MMM YYYY")
          : "",
    }));
  };

  const getH1Stocks = () => {
    const filteredList = (stocks ?? [])?.filter(
      (stc) => (stc?.drugSchedule ?? "")?.toLowerCase() === "schedule h1",
    );
    const reqList = filteredList?.map((li) => {
      return {
        name: li?.tradeName ?? li?.productName ?? "",
        value: li?.stockId ?? null,
        stockId: li?.stockId ?? null,
        drugSchedule: li?.drugSchedule ?? "",
      };
    });
    return reqList;
  };

  const periodReportModal = () => (
    <CustomModal
      open={reportModalValues?.visible}
      onClose={onCloseReportModal}
      header={getReportModalHeader(reportModalValues?.type)}
      rightModal
      modalWidth={30}
    >
      <div className="ph20">
        {isDateRangeReport(reportModalValues?.type) ? (
          <div className="flex-row-between">
            <div className="w48Per">
              <div className="txt-semi-bold fs14 light-grey">
                Start Date <span className="red-color">*</span>
              </div>
              <CustomTextField
                value={reportModalValues?.startDate}
                fullWidth
                mobileDate
                handleChange={(e) =>
                  onChangeReportModal("startDate", e?.target?.value)
                }
                error={!!reportModalValues?.startDateErr}
                helperText={reportModalValues?.startDateErr}
              />
            </div>
            <div className="w48Per">
              <div className="txt-semi-bold fs14 light-grey">
                End Date <span className="red-color">*</span>
              </div>
              <CustomTextField
                value={reportModalValues?.endDate}
                fullWidth
                mobileDate
                handleChange={(e) =>
                  onChangeReportModal("endDate", e?.target?.value)
                }
                error={!!reportModalValues?.endDateErr}
                helperText={reportModalValues?.endDateErr}
              />
            </div>
          </div>
        ) : (
          <>
            <div className="txt-semi-bold fs14 light-grey">
              Year <span className="red-color">*</span>
            </div>
            <CustomSelect
              select
              list={getYearOptions()}
              value={reportModalValues?.year ?? ""}
              handleChange={(e) =>
                onChangeReportModal("year", e?.target?.value)
              }
              error={!!reportModalValues?.yearErr}
              helperText={reportModalValues?.yearErr}
            />
            <div className="txt-semi-bold fs14 light-grey mt10">
              Month <span className="red-color">*</span>
            </div>
            <CustomSelect
              select
              list={getMonthOptions(reportModalValues?.year)}
              value={reportModalValues?.month ?? ""}
              handleChange={(e) =>
                onChangeReportModal("month", e?.target?.value)
              }
              error={!!reportModalValues?.monthErr}
              helperText={reportModalValues?.monthErr}
            />
          </>
        )}
        <div className="flex1-end mt20">
          <div>
            <CustomButton
              text="Download"
              onClick={onDownloadPeriodReport}
              smallBtn
            />
          </div>
        </div>
      </div>
    </CustomModal>
  );

  const ndpsReportModal = () => (
    <CustomModal
      open={ndpsReportValues?.visible}
      onClose={onCloseNdpsReportModal}
      header="NDPS Drug Report"
      rightModal
      modalWidth={30}
    >
      <div className="ph20">
        <div className="txt-semi-bold fs14 light-grey">
          Quarter <span className="red-color">*</span>
        </div>
        <CustomSelect
          select
          list={getLastFourQuarters().map((quarter) => ({
            name: quarter.name,
            value: quarter.name,
          }))}
          value={ndpsReportValues?.quarter ?? ""}
          handleChange={(e) =>
            onChangeNdpsReportModal("quarter", e?.target?.value)
          }
          error={!!ndpsReportValues?.quarterErr}
          helperText={ndpsReportValues?.quarterErr}
        />
        <div className="flex1-end mt20">
          <div>
            <CustomButton
              text="Download"
              onClick={onDownloadNdpsReport}
              smallBtn
            />
          </div>
        </div>
      </div>
    </CustomModal>
  );

  const getCollectionsData = () => {
    return (reportCollections?.data ?? [])?.map((li) => {
      return {
        source: tabEnum?.[li?.source],
        invoiceNumber: li?.invoiceNo,
        receiptNo: li?.receiptNo,
        invoiceDate: moment(new Date(li?.invoiceDate)).format("DD MMM YYYY"),
        paymentDate: moment(new Date(li?.paymentDate)).format("DD MMM YYYY"),
        amtPaid: Number(li?.amountPaid)?.toFixed(2),
        paymentMode: li?.paymentMode,
        totalDue: Number(li?.totalDue ?? 0)?.toFixed(2),
        balanceDue: Number(li?.balanceDue ?? 0)?.toFixed(2),
        discount: Number(li?.dicount ?? 0)?.toFixed(2),
        taxes: Number(li?.taxes ?? 0)?.toFixed(2),
        customerUserName: li?.userName ?? "",
        petName: li?.petName ?? "",
      };
    });
  };

  const normalizeDayBookSource = (source) => {
    const label = tabEnum?.[source] ?? source ?? "";
    const key = String(label).toLowerCase();
    if (key.includes("clinic") || key === "treatment") return "Clinic";
    if (key.includes("pharmacy")) return "Pharmacy";
    if (key.includes("pet")) return "Petshop";
    return null;
  };

  const normalizeDayBookType = (type) => {
    const key = String(type ?? "").toLowerCase();
    if (key === "revenue" || key === "payment collection") {
      return "Payment Collection";
    }
    if (key === "credit collection" || key === "deposit collection") {
      return "Deposit Collection";
    }
    if (key === "credit usage" || key === "deposit usage") {
      return "Deposit Usage";
    }
    if (key === "refund") return "Refund";
    return null;
  };

  const getDayBookPaymentModeSummary = () => {
    const modeMap = {};
    (reportCollections?.data ?? []).forEach((li) => {
      const mode = li?.paymentMode || "Unknown";
      modeMap[mode] = (modeMap[mode] ?? 0) + Number(li?.amountPaid ?? 0);
    });
    return Object.keys(modeMap)
      .sort((a, b) => a.localeCompare(b))
      .map((mode) => ({
        paymentMode: mode,
        grandTotal: Number(modeMap[mode] ?? 0),
      }));
  };

  const getDayBookSourceTypeSummary = () => {
    const sources = ["Clinic", "Pharmacy", "Petshop"];
    const types = [
      "Payment Collection",
      "Deposit Collection",
      "Deposit Usage",
      "Refund",
    ];
    const matrix = types.reduce((acc, type) => {
      acc[type] = { Clinic: 0, Pharmacy: 0, Petshop: 0 };
      return acc;
    }, {});

    (reportCollections?.data ?? []).forEach((li) => {
      const type = normalizeDayBookType(li?.type);
      const source = normalizeDayBookSource(li?.source);
      if (!type || !source) return;
      matrix[type][source] += Number(li?.amountPaid ?? 0);
    });

    const rows = types.map((type) => ({
      type,
      Clinic: matrix[type].Clinic,
      Pharmacy: matrix[type].Pharmacy,
      Petshop: matrix[type].Petshop,
    }));

    const grandTotal = {
      type: "Grand Total",
      Clinic: rows.reduce((sum, row) => sum + Number(row.Clinic ?? 0), 0),
      Pharmacy: rows.reduce((sum, row) => sum + Number(row.Pharmacy ?? 0), 0),
      Petshop: rows.reduce((sum, row) => sum + Number(row.Petshop ?? 0), 0),
    };

    return { sources, rows, grandTotal };
  };

  const normalizeDayBookPayStatus = (status) => {
    const key = String(status ?? "").toLowerCase();
    if (key === "cancelled") return "Cancelled";
    if (key === "paid") return "Paid";
    if (
      key === "unpaid" ||
      key === "partiallypaid" ||
      key === "partially paid"
    ) {
      return "Unpaid";
    }
    return null;
  };

  const getDayBookBillsStatusSummary = () => {
    const sources = ["Clinic", "Pharmacy", "Petshop"];
    const statuses = ["Unpaid", "Paid", "Cancelled"];
    const matrix = statuses.reduce((acc, status) => {
      acc[status] = { Clinic: 0, Pharmacy: 0, Petshop: 0 };
      return acc;
    }, {});

    (reportPayments?.data ?? []).forEach((pay) => {
      const status = normalizeDayBookPayStatus(pay?.payStatus);
      const source = normalizeDayBookSource(
        pay?.type ?? pay?.tab ?? pay?.source,
      );
      if (!status || !source) return;
      matrix[status][source] += Number(pay?.totalAmt ?? pay?.totalDue ?? 0);
    });

    const rows = statuses.map((status) => ({
      status,
      Clinic: matrix[status].Clinic,
      Pharmacy: matrix[status].Pharmacy,
      Petshop: matrix[status].Petshop,
    }));

    const grandTotal = {
      status: "Grand Total",
      Clinic: rows.reduce((sum, row) => sum + Number(row.Clinic ?? 0), 0),
      Pharmacy: rows.reduce((sum, row) => sum + Number(row.Pharmacy ?? 0), 0),
      Petshop: rows.reduce((sum, row) => sum + Number(row.Petshop ?? 0), 0),
    };

    return { sources, rows, grandTotal };
  };

  const formatDayBookAmount = (value) => Number(value ?? 0).toFixed(2);

  const dayBookHtmlTableStyles = {
    cell: {
      border: "1px solid #d9d9d9",
      padding: "8px 10px",
      textAlign: "left",
      color: "#000",
      fontWeight: 400,
      fontSize: "12px",
    },
    head: {
      border: "1px solid #d9d9d9",
      padding: "8px 10px",
      textAlign: "left",
      background: "#f5f5f5",
      color: "#000",
      fontWeight: 700,
      fontSize: "12px",
    },
    bold: {
      border: "1px solid #d9d9d9",
      padding: "8px 10px",
      textAlign: "left",
      color: "#000",
      fontWeight: 700,
      fontSize: "12px",
    },
  };

  const getPaymentModes = () => {
    const reqList = (paymentMethods ?? [])
      ?.filter((li) => li?.status)
      ?.map((li) => (li?.name ?? "")?.toLowerCase())
      ?.filter(Boolean);
    // const uniqList = uniq(reqList);
    const reqDropList = reqList?.map((li) => ({
      name: capitalize(li),
      value: li,
    }));
    return reqDropList?.sort((a, b) =>
      (a?.name ?? "")?.localeCompare(b?.name ?? "", undefined, {
        sensitivity: "base",
      }),
    );
  };

  const dayBookReportModal = () => (
    <CustomModal
      open={dayBookReportValues?.visible}
      onClose={onCloseDayBookReportModal}
      header="Day Book"
      modal={dayBookReportValues?.valueEntered}
      rightModal={!dayBookReportValues?.valueEntered}
      modalWidth={dayBookReportValues?.valueEntered ? 95 : 30}
    >
      <div className="scroll-90vh">
        {dayBookReportValues?.valueEntered ? (
          <div>
            <div className="flex-row-between flex-row-ali-cen ph10 mt15">
              <div className="flex-row-ali-cen flex1">
                <Typography
                  className={`ml20 newTab ph10 cursor ${
                    dayBookReportValues?.activeTab === "Bills"
                      ? "newTabSelected blue-color"
                      : ""
                  }`}
                  onClick={() =>
                    onChangeDayBookReportModal("activeTab", "Bills")
                  }
                >
                  Bills
                </Typography>
                <Typography
                  className={`ml20 newTab ph10 cursor ${
                    dayBookReportValues?.activeTab === "Collections"
                      ? "newTabSelected blue-color"
                      : ""
                  }`}
                  onClick={() =>
                    onChangeDayBookReportModal("activeTab", "Collections")
                  }
                >
                  Collections
                </Typography>
                <Typography className="ml20 txt-semi-bold fs14">
                  Date{" "}
                  {moment(new Date(dayBookReportValues?.date)).format(
                    "YYYY-MM-DD",
                  )}
                </Typography>
                <Typography className="ml20 txt-semi-bold fs14">
                  Total Amount Rs.{" "}
                  {dayBookReportValues?.activeTab === "Collections"
                    ? Object.values(
                        (reportCollections?.summary?.categories ?? [])?.find(
                          (c) => c?.category === "Grand Total",
                        )?.sources ?? {},
                      )
                        ?.reduce((acc, curr) => acc + Number(curr ?? 0), 0)
                        ?.toFixed(2)
                    : (reportPayments?.data ?? [])
                        ?.reduce(
                          (acc, curr) =>
                            acc + Number(curr?.totalAmt ?? curr?.totalDue ?? 0),
                          0,
                        )
                        ?.toFixed(2)}
                </Typography>
              </div>
              <div className="flex-row-ali-cen">
                <div style={{ width: 200, minWidth: 200 }}>
                  <CustomSelect
                    list={paymentSourceTypes}
                    value={dayBookReportValues?.source ?? []}
                    name="source"
                    handleChange={(e) =>
                      onChangeDayBookReportModal("source", e?.target?.value)
                    }
                    label="Source"
                    multiSelectSearchCheck
                  />
                </div>
                <div style={{ width: 200, minWidth: 200 }} className="ml15">
                  {dayBookReportValues?.activeTab === "Collections" ? (
                    <CustomSelect
                      list={getPaymentModes()}
                      value={dayBookReportValues?.paymentMode ?? []}
                      name="paymentMode"
                      handleChange={(e) =>
                        onChangeDayBookReportModal(
                          "paymentMode",
                          e?.target?.value,
                        )
                      }
                      label="Payment Mode"
                      multiSelectSearchCheck
                    />
                  ) : (
                    <CustomSelect
                      list={openClosedDropList}
                      value={dayBookReportValues?.type}
                      name="type"
                      handleChange={(e) =>
                        onChangeDayBookReportModal("type", e?.target?.value)
                      }
                      label="Type"
                      select
                    />
                  )}
                </div>
                <div className="ml15 mr20">
                  <CustomButton
                    text="⬇"
                    onClick={onDownloadDayBookReport}
                    smallBtn
                  />
                </div>
              </div>
            </div>
            <div className="mt10" />
            {dayBookReportValues?.activeTab === "Collections" ? (
              <div>
                <div className="ph5">
                  {(() => {
                    const paymentModeSummary =
                      reportCollections?.summary?.paymentModeTotals ?? [];
                    const isAdvanceAdjustmentMode = (mode) =>
                      String(mode ?? "")
                        ?.trim()
                        ?.toLowerCase() === "advance adjustment";
                    const paymentModeRows = paymentModeSummary?.filter(
                      (row) => !isAdvanceAdjustmentMode(row?.paymentMode),
                    );
                    const advanceAdjustmentRows = paymentModeSummary?.filter(
                      (row) => isAdvanceAdjustmentMode(row?.paymentMode),
                    );
                    const paymentModeGrandTotal = paymentModeRows?.reduce(
                      (acc, row) => acc + Number(row?.grandTotal ?? 0),
                      0,
                    );
                    const categories =
                      reportCollections?.summary?.categories ?? [];
                    const sourceKeys = Object.keys(
                      categories.find((c) => c?.sources)?.sources ?? {},
                    );
                    const categoryLabelMap = {
                      "Total Payment Collection": "Payment Collection",
                      "Total Deposit Collection": "Deposit Collection",
                      "Total Deposit Usage": "Deposit Usage",
                      "Total Refund": "Refund",
                    };
                    const getCategoryLabel = (category) =>
                      categoryLabelMap[category] ?? category;
                    const isDepositUsageCategory = (category) => {
                      const label = getCategoryLabel(category);
                      return (
                        label === "Deposit Usage" ||
                        String(category ?? "")
                          ?.trim()
                          ?.toLowerCase() === "total deposit usage"
                      );
                    };
                    const allCategoryRows = categories?.filter(
                      (c) => c?.category !== "Grand Total",
                    );
                    const categoryRows = allCategoryRows?.filter(
                      (c) => !isDepositUsageCategory(c?.category),
                    );
                    const depositUsageRows = allCategoryRows?.filter((c) =>
                      isDepositUsageCategory(c?.category),
                    );
                    const grandTotalCategory = {
                      category: "Grand Total",
                      sources: sourceKeys?.reduce((acc, source) => {
                        acc[source] = categoryRows.reduce(
                          (sum, row) =>
                            sum + Number(row?.sources?.[source] ?? 0),
                          0,
                        );
                        return acc;
                      }, {}),
                    };
                    const cellStyle = {
                      border: "1px solid #d9d9d9",
                      padding: "8px 10px",
                      textAlign: "left",
                      color: "#000",
                      fontWeight: 400,
                      fontSize: "12px",
                    };
                    const headStyle = {
                      ...cellStyle,
                      background: "#f5f5f5",
                      color: "#000",
                      fontWeight: 700,
                      fontSize: "12px",
                    };
                    const boldCellStyle = {
                      ...cellStyle,
                      fontWeight: 700,
                      fontSize: "12px",
                    };
                    const emptyRowStyle = {
                      ...cellStyle,
                      borderLeft: "1px solid #d9d9d9",
                      borderRight: "1px solid #d9d9d9",
                      borderTop: "none",
                      borderBottom: "none",
                      height: 16,
                      padding: 0,
                    };
                    return (
                      <div className="flex-row ph20 mb15">
                        <div className="w30Per pr10">
                          <table
                            style={{
                              width: "100%",
                              borderCollapse: "collapse",
                              border: "1px solid #d9d9d9",
                            }}
                          >
                            <thead>
                              <tr>
                                <th style={headStyle}>Payment Mode</th>
                                <th style={headStyle}>Grand Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {paymentModeSummary?.length === 0 ? (
                                <tr>
                                  <td
                                    colSpan={2}
                                    style={{
                                      ...cellStyle,
                                      textAlign: "center",
                                    }}
                                  >
                                    No records available
                                  </td>
                                </tr>
                              ) : (
                                <>
                                  {paymentModeRows?.map((row) => (
                                    <tr key={row?.paymentMode}>
                                      <td
                                        className="capitalize"
                                        style={cellStyle}
                                      >
                                        {row?.paymentMode}
                                      </td>
                                      <td style={cellStyle}>
                                        {formatDayBookAmount(row?.grandTotal)}
                                      </td>
                                    </tr>
                                  ))}
                                  <tr>
                                    <td style={boldCellStyle}>Grand Total</td>
                                    <td style={boldCellStyle}>
                                      {formatDayBookAmount(
                                        paymentModeGrandTotal,
                                      )}
                                    </td>
                                  </tr>
                                  {advanceAdjustmentRows?.length > 0 ? (
                                    <>
                                      <tr>
                                        <td colSpan={2} style={emptyRowStyle}>
                                          &nbsp;
                                        </td>
                                      </tr>
                                      {advanceAdjustmentRows?.map((row) => (
                                        <tr key={row?.paymentMode}>
                                          <td
                                            className="capitalize"
                                            style={cellStyle}
                                          >
                                            {row?.paymentMode}
                                          </td>
                                          <td style={cellStyle}>
                                            {formatDayBookAmount(
                                              row?.grandTotal,
                                            )}
                                          </td>
                                        </tr>
                                      ))}
                                    </>
                                  ) : null}
                                </>
                              )}
                            </tbody>
                          </table>
                        </div>
                        <div className="w70Per pl10">
                          <table
                            style={{
                              width: "100%",
                              borderCollapse: "collapse",
                              border: "1px solid #d9d9d9",
                            }}
                          >
                            <thead>
                              <tr>
                                <th style={headStyle} />
                                {sourceKeys.map((source) => (
                                  <th key={source} style={headStyle}>
                                    {source}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {categories.length === 0 ? (
                                <tr>
                                  <td
                                    colSpan={Math.max(sourceKeys.length, 1) + 1}
                                    style={{
                                      ...cellStyle,
                                      textAlign: "center",
                                    }}
                                  >
                                    No records available
                                  </td>
                                </tr>
                              ) : (
                                <>
                                  {categoryRows.map((row) => (
                                    <tr key={row.category}>
                                      <td style={cellStyle}>
                                        {getCategoryLabel(row.category)}
                                      </td>
                                      {sourceKeys.map((source) => (
                                        <td
                                          key={`${row.category}-${source}`}
                                          style={cellStyle}
                                        >
                                          {formatDayBookAmount(
                                            row?.sources?.[source],
                                          )}
                                        </td>
                                      ))}
                                    </tr>
                                  ))}
                                  <tr>
                                    <td style={boldCellStyle}>
                                      {grandTotalCategory.category}
                                    </td>
                                    {sourceKeys.map((source) => (
                                      <td
                                        key={`grand-${source}`}
                                        style={boldCellStyle}
                                      >
                                        {formatDayBookAmount(
                                          grandTotalCategory?.sources?.[source],
                                        )}
                                      </td>
                                    ))}
                                  </tr>
                                  {depositUsageRows?.length > 0 ? (
                                    <>
                                      <tr>
                                        <td
                                          colSpan={
                                            Math.max(sourceKeys.length, 1) + 1
                                          }
                                          style={emptyRowStyle}
                                        >
                                          &nbsp;
                                        </td>
                                      </tr>
                                      {depositUsageRows.map((row) => (
                                        <tr key={row.category}>
                                          <td style={cellStyle}>
                                            {getCategoryLabel(row.category)}
                                          </td>
                                          {sourceKeys.map((source) => (
                                            <td
                                              key={`${row.category}-${source}`}
                                              style={cellStyle}
                                            >
                                              {formatDayBookAmount(
                                                row?.sources?.[source],
                                              )}
                                            </td>
                                          ))}
                                        </tr>
                                      ))}
                                    </>
                                  ) : null}
                                </>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })()}
                </div>
                <ReportTable
                  datas={getCollectionsData()}
                  columns={colctHdrs}
                  maxHeight={450}
                />
              </div>
            ) : (
              <div>
                <div className="ph5">
                  {(() => {
                    const billSummaries =
                      reportPayments?.summary?.billSummaries ?? [];
                    const sourceLabelMap = {
                      Clinic: "Clinic",
                      Pharmacy: "Pharmacy",
                      "Pet Shop": "Petshop",
                      Petshop: "Petshop",
                    };
                    const getSourceLabel = (summary) => {
                      const fromHeading = String(summary?.heading ?? "")
                        .replace(/\s*Summary$/i, "")
                        .trim();
                      if (fromHeading) {
                        return sourceLabelMap[fromHeading] ?? fromHeading;
                      }
                      return (
                        sourceLabelMap[summary?.source] ?? summary?.source ?? ""
                      );
                    };
                    const sources = billSummaries
                      .map(getSourceLabel)
                      .filter(Boolean);
                    const statuses = ["Unpaid", "Paid", "Cancelled"];
                    const rows = statuses.map((status) => {
                      const row = { status };
                      billSummaries.forEach((summary) => {
                        const source = getSourceLabel(summary);
                        const match = (summary?.statuses ?? []).find(
                          (s) => s?.status === status,
                        );
                        row[source] = Number(match?.total ?? 0);
                      });
                      return row;
                    });
                    const grandTotal = {
                      status: "Grand Total",
                    };
                    sources.forEach((source) => {
                      grandTotal[source] = rows.reduce(
                        (sum, row) => sum + Number(row[source] ?? 0),
                        0,
                      );
                    });
                    const { cell, head, bold } = dayBookHtmlTableStyles;
                    return (
                      <div className="ph20 mb15">
                        <div className="w40Per">
                          <table
                            style={{
                              width: "100%",
                              borderCollapse: "collapse",
                              border: "1px solid #d9d9d9",
                            }}
                          >
                            <thead>
                              <tr>
                                <th style={head} />
                                {sources.map((source) => (
                                  <th key={source} style={head}>
                                    {source}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {billSummaries.length === 0 ? (
                                <tr>
                                  <td
                                    colSpan={Math.max(sources.length, 1) + 1}
                                    style={{
                                      ...cell,
                                      textAlign: "center",
                                    }}
                                  >
                                    No records available
                                  </td>
                                </tr>
                              ) : (
                                <>
                                  {rows.map((row) => (
                                    <tr key={row.status}>
                                      <td style={cell}>{row.status}</td>
                                      {sources.map((source) => (
                                        <td
                                          key={`${row.status}-${source}`}
                                          style={cell}
                                        >
                                          {formatDayBookAmount(row[source])}
                                        </td>
                                      ))}
                                    </tr>
                                  ))}
                                  <tr>
                                    <td style={bold}>{grandTotal.status}</td>
                                    {sources.map((source) => (
                                      <td key={`grand-${source}`} style={bold}>
                                        {formatDayBookAmount(
                                          grandTotal[source],
                                        )}
                                      </td>
                                    ))}
                                  </tr>
                                </>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })()}
                </div>
                <div className="mt20" />
                <Container maxWidth="xl">
                  <TableContainer sx={{ maxHeight: "65vh" }}>
                    <Table
                      sx={{ minWidth: 200 }}
                      aria-labelledby="tableTitle"
                      stickyHeader
                    >
                      <TableHead>
                        <TableRow>
                          {paymentHdrs.map((hl, i) => (
                            <TableCell
                              key={i}
                              style={tblGrayBack}
                              sortDirection={false}
                              className="table-header-text"
                            >
                              <div style={{ width: "auto" }}>
                                {nameExpan?.[hl]}
                              </div>
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      {(reportPayments?.data ?? []).length === 0 && (
                        <TableRow>
                          <TableCell
                            colSpan={7}
                            style={{ textAlign: "center", height: 100 }}
                          >
                            <div className="no-rec">No records available</div>
                          </TableCell>
                        </TableRow>
                      )}
                      <TableBody>
                        {(reportPayments?.data ?? []).map((pay, ind) => (
                          <TableRow tabIndex={-1} key={ind}>
                            {paymentHdrs.map((th, index) => (
                              <TableCell
                                key={index + th + "tr"}
                                component="th"
                                className="table-text-black capitalize"
                              >
                                <div
                                  className="upload-row"
                                  style={{ width: "auto" }}
                                >
                                  {th === "petName" ? (
                                    <div>
                                      <div
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                        }}
                                      >
                                        <span className="txt-semi-bold capitalize fs14">
                                          {pay?.petName}
                                        </span>
                                      </div>
                                      {pay?.userName?.length > 0 ? (
                                        <div className="flex-row-ali-cen mt5">
                                          <span className="txt-semi-bold capitalize fs12">
                                            {pay?.userName}
                                          </span>
                                        </div>
                                      ) : null}
                                    </div>
                                  ) : th === "vetName" ? (
                                    <div>
                                      {pay?.doctorName?.length > 0
                                        ? `${getRightSaluation(clinicVets, pay?.doctorId)}${pay?.doctorName}`
                                        : ""}
                                    </div>
                                  ) : th === "totalDue" ? (
                                    <div>
                                      {Number(
                                        pay?.totalAmt ?? pay?.totalDue ?? 0,
                                      )?.toFixed(2)}
                                    </div>
                                  ) : th === "balanceDue" ? (
                                    <div>
                                      {Number(
                                        pay?.balDue ?? pay?.balanceDue ?? 0,
                                      )?.toFixed(2)}
                                    </div>
                                  ) : th === "payType" ? (
                                    <div className="table-text-black-semibold capitalize">
                                      {tabEnum?.[pay?.type] ?? ""}
                                    </div>
                                  ) : th === "trDate" ? (
                                    <div>
                                      {pay?.createdDate
                                        ? moment(pay?.createdDate).format(
                                            "DD MMM YYYY",
                                          )
                                        : ""}
                                    </div>
                                  ) : (
                                    pay?.[th]
                                  )}
                                </div>
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Container>
              </div>
            )}
          </div>
        ) : (
          <div className="ph20">
            <div className="txt-semi-bold fs14 light-grey">
              Date <span className="red-color">*</span>
            </div>
            <CustomTextField
              value={dayBookReportValues?.date}
              fullWidth
              mobileDate
              handleChange={(e) =>
                onChangeDayBookReportModal("date", e?.target?.value)
              }
              error={!!dayBookReportValues?.dateErr}
              helperText={dayBookReportValues?.dateErr}
            />
            <div className="mt20" />
            <CustomSelect
              list={paymentSourceTypes}
              value={dayBookReportValues?.source ?? []}
              name="source"
              handleChange={(e) =>
                onChangeDayBookReportModal("source", e?.target?.value)
              }
              label="Source"
              multiSelectSearchCheck
            />
            <div className="flex1-end mt20">
              <div>
                <CustomButton
                  text="Generate"
                  onClick={onGenerateDayBookReport}
                  smallBtn
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </CustomModal>
  );

  const billsReportModal = () => (
    <CustomModal
      open={billsReportValues?.visible}
      onClose={onCloseBillsReportModal}
      header="Bills Report"
      rightModal
      modalWidth={30}
    >
      <div className="ph20">
        <div className="txt-semi-bold fs14 light-grey">
          Period <span className="red-color">*</span>
        </div>
        <CustomCheckbox
          radio
          isRow
          name="period"
          value={billsReportValues?.period}
          onChange={(e) => onChangeBillsReportModal("period", e?.target?.value)}
          radios={[
            { label: "Custom", value: "custom" },
            { label: "Day", value: "day" },
            { label: "Week", value: "week" },
            { label: "Month", value: "month" },
          ]}
        />
        {billsReportValues?.period === "custom" ? (
          <div className="flex-row-between mt10">
            <div className="w48Per">
              <div className="txt-semi-bold fs14 light-grey">
                Start Date <span className="red-color">*</span>
              </div>
              <CustomTextField
                value={billsReportValues?.startDate}
                fullWidth
                mobileDate
                handleChange={(e) =>
                  onChangeBillsReportModal("startDate", e?.target?.value)
                }
                error={!!billsReportValues?.startDateErr}
                helperText={billsReportValues?.startDateErr}
              />
            </div>
            <div className="w48Per">
              <div className="txt-semi-bold fs14 light-grey">
                End Date <span className="red-color">*</span>
              </div>
              <CustomTextField
                value={billsReportValues?.endDate}
                fullWidth
                mobileDate
                handleChange={(e) =>
                  onChangeBillsReportModal("endDate", e?.target?.value)
                }
                error={!!billsReportValues?.endDateErr}
                helperText={billsReportValues?.endDateErr}
              />
            </div>
          </div>
        ) : null}
        <div className="mt20" />
        <CustomSelect
          list={paymentSourceTypes}
          value={billsReportValues?.source ?? []}
          name="source"
          handleChange={(e) =>
            onChangeBillsReportModal("source", e?.target?.value)
          }
          label="Source"
          multiSelectSearchCheck
        />
        <div className="mt20" />
        <CustomSelect
          list={[{ name: "All", value: "All" }, , ...openClosedDropList]}
          value={billsReportValues?.billType}
          name="billType"
          handleChange={(e) =>
            onChangeBillsReportModal("billType", e?.target?.value)
          }
          label="Bill Type"
          select
        />
        <div className="flex1-end mt20">
          <div>
            <CustomButton
              text="Download"
              onClick={onDownloadBillsReport}
              smallBtn
            />
          </div>
        </div>
      </div>
    </CustomModal>
  );

  const soaReportModal = () => {
    const isPetSearch = soaReportValues?.searchType === "pet";
    const selectedPet = soaReportValues?.pet;
    const selectedUser = soaReportValues?.user;
    const hasSelection = isPetSearch
      ? !!selectedPet?.petId
      : !!selectedUser?.id;
    const showSoa = soaReportValues?.dateSelected;

    return (
      <CustomModal
        open={soaReportValues?.visible}
        onClose={onCloseSoaReportModal}
        header="Statement of Account (SOA)"
        rightModal={!showSoa}
        modal={!!showSoa}
        modalWidth={showSoa ? 80 : 30}
        headerBold={!!showSoa}
      >
        <div className={`${showSoa ? "scroll-70vh" : ""} ph20`}>
          {!showSoa ? (
            <>
              <div className="txt-semi-bold fs14 light-grey">
                Search Type <span className="red-color">*</span>
              </div>
              <CustomSelect
                list={[
                  { name: "Pet", value: "pet" },
                  { name: "Customer", value: "user" },
                ]}
                value={soaReportValues?.searchType}
                handleChange={(e) =>
                  onChangeSoaReport("searchType", e?.target?.value)
                }
                select
              />
              <div className="txt-semi-bold fs14 light-grey mt10">
                Search {isPetSearch ? "Pet" : "Customer"}{" "}
                <span className="red-color">*</span>
              </div>
              <CustomTextField
                fullWidth
                value={soaReportValues?.searchValue}
                handleChange={(e) =>
                  onChangeSoaReport("searchValue", e?.target?.value)
                }
                placeholder={`Search ${isPetSearch ? "Pet" : "Customer"} here...`}
                error={!!soaReportValues?.selectionErr}
                helperText={soaReportValues?.selectionErr}
              />
              {soaReportValues?.searchValue?.length > 0 &&
              soaReportValues?.searchEnabled &&
              isPetSearch ? (
                <div className="mt10 scroll-30vh">
                  {(clinicPetList?.pets ?? [])?.map((petDat) => {
                    const pet = petDat?.listOfDatas;
                    return (
                      <div
                        className="flex-row-ali-cen cursor mv10 cus-box"
                        key={pet?.petId}
                        onClick={() => onChangeSoaReport("pet", pet)}
                      >
                        <div className="flex-coloum ml10">
                          <Typography className="blue-color txt-semi-bold fs12">
                            Pet Name:{" "}
                            <span className="txt-regular black15">
                              {pet?.petName}
                            </span>
                          </Typography>
                          <Typography className="blue-color txt-semi-bold fs12">
                            Parent Name:{" "}
                            <span className="txt-regular black15">
                              {pet?.userName}
                            </span>
                          </Typography>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : null}
              {soaReportValues?.searchValue?.length > 0 &&
              soaReportValues?.searchEnabled &&
              !isPetSearch ? (
                <div className="mt10 scroll-30vh">
                  {soaUserSearchResults()?.map((usr) => (
                    <div
                      className="flex-row-ali-cen cursor mv10 cus-box"
                      key={usr?.id}
                      onClick={() => onChangeSoaReport("user", usr)}
                    >
                      <div className="flex-coloum ml10">
                        <Typography className="blue-color txt-semi-bold fs12">
                          Parent Name:{" "}
                          <span className="txt-regular black15">
                            {usr?.name}
                          </span>
                        </Typography>
                        <Typography className="blue-color txt-semi-bold fs12">
                          Phone:{" "}
                          <span className="txt-regular black15">
                            {usr?.mobile}
                          </span>
                        </Typography>
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}
              {hasSelection && !soaReportValues?.searchEnabled ? (
                <div className="flex-row-ali-cen cursor mv10 cus-box-selected light-blue-back6">
                  <div className="flex-coloum ml10">
                    {selectedPet?.petId ? (
                      <Typography className="blue-color txt-semi-bold fs12">
                        Pet Name:{" "}
                        <span className="txt-regular black15">
                          {selectedPet?.petName}
                        </span>
                      </Typography>
                    ) : null}
                    <Typography className="blue-color txt-semi-bold fs12">
                      Parent Name:{" "}
                      <span className="txt-regular black15">
                        {selectedUser?.name ?? selectedPet?.userName}
                      </span>
                    </Typography>
                    {selectedUser?.id ? (
                      <Typography className="blue-color txt-semi-bold fs12">
                        Phone:{" "}
                        <span className="txt-regular black15">
                          {selectedUser?.mobile ?? ""}
                        </span>
                      </Typography>
                    ) : null}
                  </div>
                </div>
              ) : null}
              <div className="flex-row-between mt10">
                <div className="w48Per">
                  <div className="txt-semi-bold fs14 light-grey">
                    Start Date <span className="red-color">*</span>
                  </div>
                  <CustomTextField
                    value={soaReportValues?.startDate}
                    fullWidth
                    mobileDate
                    handleChange={(e) =>
                      onChangeSoaReport("startDate", e?.target?.value)
                    }
                    error={!!soaReportValues?.startDateErr}
                    helperText={soaReportValues?.startDateErr}
                  />
                </div>
                <div className="w48Per">
                  <div className="txt-semi-bold fs14 light-grey">
                    End Date <span className="red-color">*</span>
                  </div>
                  <CustomTextField
                    value={soaReportValues?.endDate}
                    fullWidth
                    mobileDate
                    handleChange={(e) =>
                      onChangeSoaReport("endDate", e?.target?.value)
                    }
                    error={!!soaReportValues?.endDateErr}
                    helperText={soaReportValues?.endDateErr}
                  />
                </div>
              </div>
              <div className="flex1-end mt20">
                <div>
                  <CustomButton
                    text="Proceed"
                    onClick={onProceedSoaReport}
                    smallBtn
                    // disabled={!isPetSearch}
                  />
                </div>
              </div>
            </>
          ) : (
            <StatementOfAccounts
              ref={soaPrintRef}
              clinicDet={clinicDet}
              petName={selectedPet?.petName}
              userName={
                isPetSearch ? selectedPet?.userName : selectedUser?.name
              }
              startDate={soaReportValues?.startDate}
              endDate={soaReportValues?.endDate}
              soaList={soaList}
              onPrint={handleSoaPrint}
              showPetNameColumn={!isPetSearch}
            />
          )}
        </div>
      </CustomModal>
    );
  };

  const commonTopBar = () => {
    return (
      <TopBar sx={activeTab === "Master" ? { pb: 1 } : undefined}>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <div className="flex-row-ali-cen">
              <div
                className="ph10pv3"
                onClick={() => handleChangeTab("Dashboard")}
              >
                <div
                  className={`flex-center cursor ${
                    activeTab === "Dashboard"
                      ? "pet-det-sel-tab"
                      : "pet-det-un-sel-tab"
                  }`}
                >
                  Dashboard
                </div>
              </div>
              <div
                className="ph10pv3"
                onClick={() => handleChangeTab("Payments")}
              >
                <div
                  className={`flex-center cursor ${
                    activeTab === "Payments"
                      ? "pet-det-sel-tab"
                      : "pet-det-un-sel-tab"
                  }`}
                >
                  Payments
                </div>
              </div>
              <div
                className="ph10pv3"
                onClick={() => handleChangeTab("Returns")}
              >
                <div
                  className={`flex-center cursor ${
                    activeTab === "Returns"
                      ? "pet-det-sel-tab"
                      : "pet-det-un-sel-tab"
                  }`}
                >
                  Returns
                </div>
              </div>
              {activeTab === "PointOfSales" ? (
                <div
                  className="ph10pv3"
                  // onClick={() => handleChangeTab("PointOfSales")}
                >
                  <div
                    className={`flex-center cursor ${
                      activeTab === "PointOfSales"
                        ? "pet-det-sel-tab"
                        : "pet-det-un-sel-tab"
                    }`}
                  >
                    Point of Sale
                  </div>
                </div>
              ) : null}
              <div
                className="ph10pv3"
                onClick={() => handleChangeTab("Reports")}
              >
                <div
                  className={`flex-center cursor ${
                    activeTab === "Reports"
                      ? "pet-det-sel-tab"
                      : "pet-det-un-sel-tab"
                  }`}
                >
                  Reports
                </div>
              </div>
              <div className="ph10pv3">
                <div
                  onMouseEnter={(e) => {
                    setMasterAnchorEl(e.currentTarget);
                  }}
                  className={`flex-center cursor ${
                    activeTab === "Master"
                      ? "pet-det-sel-tab"
                      : "pet-det-un-sel-tab"
                  }`}
                >
                  Master <ArrowDropDownIcon fontSize="small" />
                </div>
                <Menu
                  anchorEl={masterAnchorEl}
                  open={Boolean(masterAnchorEl)}
                  onClose={() => setMasterAnchorEl(null)}
                  MenuListProps={{
                    onMouseLeave: () => setMasterAnchorEl(null),
                  }}
                >
                  <MenuItem
                    onClick={() => handleChangeMaster("Payment Method")}
                  >
                    Payment Method
                  </MenuItem>
                  <MenuItem onClick={() => handleChangeMaster("Discount")}>
                    Discount
                  </MenuItem>
                </Menu>
              </div>
              <div className="ph10pv3">
                <div
                  onMouseEnter={(e) => {
                    setReportAnchorEl(e.currentTarget);
                  }}
                  className={`flex-center cursor ${
                    activeTab === "Download Reports"
                      ? "pet-det-sel-tab"
                      : "pet-det-un-sel-tab"
                  }`}
                >
                  Download Reports <ArrowDropDownIcon fontSize="small" />
                </div>
                <Menu
                  anchorEl={reportAnchorEl}
                  open={Boolean(reportAnchorEl)}
                  onClose={() => setReportAnchorEl(null)}
                  MenuListProps={{
                    onMouseLeave: () => setReportAnchorEl(null),
                  }}
                >
                  {hasInventoryCreateRole ? (
                    <>
                      <MenuItem
                        onClick={() =>
                          onOpenDateRangeReport("accounts-receivables")
                        }
                      >
                        Accounts Receivables Report
                      </MenuItem>
                      <MenuItem onClick={onOpenBillsReport}>
                        Bills Report
                      </MenuItem>
                      <MenuItem
                        onClick={() =>
                          onOpenDateRangeReport(
                            "consolidated-financial-summary",
                          )
                        }
                      >
                        Consolidated Financial Summary
                      </MenuItem>
                      <MenuItem onClick={onOpenDayBookReport}>
                        Day Book
                      </MenuItem>
                      <MenuItem onClick={() => onOpenPeriodReport("gst")}>
                        GST Report
                      </MenuItem>
                      <MenuItem onClick={() => onOpenPeriodReport("hsn")}>
                        HSN Summary
                      </MenuItem>
                    </>
                  ) : null}
                  <MenuItem onClick={onOpenNdpsReport}>
                    NDPS Drug Report
                  </MenuItem>
                  <MenuItem onClick={onOpenH1Report}>
                    Schedule H1 Report
                  </MenuItem>
                  <MenuItem onClick={onOpenSoaReport}>
                    Statement of Account (SOA)
                  </MenuItem>
                </Menu>
              </div>
            </div>
          </Grid>
        </Grid>
      </TopBar>
    );
  };

  // If Reports is selected, render only the App component
  if (!activeTab) {
    return (
      <Navigate
        to={`/payments/${PAYMENTS_TAB_PATHS.Dashboard}`}
        replace
        state={location.state}
      />
    );
  }

  if (activeTab === "Reports") {
    return (
      <>
        {commonTopBar()}
        <div
          style={{
            maxHeight: "calc(100vh - 120px)",
            overflowY: "auto",
            overflowX: "hidden",
            padding: "0 10px 0 10px",
          }}
        >
          <Main />
        </div>
        {periodReportModal()}
        {ndpsReportModal()}
        {dayBookReportModal()}
        {billsReportModal()}
        {soaReportModal()}
      </>
    );
  }

  // For other tabs, render with VetAndUpcomingAppointments wrapper
  return (
    <>
      <VetAndUpcomingAppointments
        left={false}
        handleEdit={handleEdit}
        handleDelete={handleDelete}
        right={false}
        // right={activeTab === "PointOfSales"}
      >
        {commonTopBar()}
        <div
        // style={{
        //   maxHeight: "calc(100vh - 120px)",
        //   overflowY: "auto",
        //   overflowX: "hidden",
        //   padding: "0 10px 0 10px",
        // }}
        >
          {activeTab === "Payments" ? (
            <Payments
              posPayId={posPayId}
              setPosPayId={setPosPayId}
              paymentTableType={paymentTableType}
              setPaymentTableType={setPaymentTableType}
              setSelectedPaymentTab={setSelectedPaymentTab}
              selectedPaymentTab={selectedPaymentTab}
              setPaymentId={setPaymentId}
              setSelecetdPayment={setSelecetdPayment}
              setPosModVsble={setPosModVsble}
              setActiveTab={setActiveTab}
            />
          ) : null}
          {activeTab === "Returns" ? <Returns /> : null}
          {activeTab === "PointOfSales" ? (
            <PointOfSaleNew
              selectedPayment={selectedPayment}
              setSelecetdPayment={setSelecetdPayment}
              setActiveTab={setActiveTab}
            />
          ) : // <PointOfSales
          // selectedPayment={selectedPayment}
          // setSelecetdPayment={setSelecetdPayment}
          //   paymentId={paymentId}
          //   setPaymentId={setPaymentId}
          //   setActiveTab={setActiveTab}
          //   setPosPayId={setPosPayId}
          //   setPosModVsble={setPosModVsble}
          //   posModVsble={posModVsble}
          // />
          null}
          {activeTab === "Master" ? (
            <>
              {masterSubTab === "Payment Method" ? (
                <PaymentMethod />
              ) : (
                <Discount />
              )}
            </>
          ) : null}
          {activeTab === "Dashboard" ? (
            <PaymentDashboard
              setActiveTab={setActiveTab}
              setPosModVsble={setPosModVsble}
              setSelecetdPayment={setSelecetdPayment}
              setPaymentId={setPaymentId}
              setPaymentTableType={setPaymentTableType}
              setSelectedPaymentTab={setSelectedPaymentTab}
            />
          ) : null}
        </div>
      </VetAndUpcomingAppointments>

      <CustomModal
        open={h1ReportModValues?.visible}
        onClose={onCloseH1Mod}
        header="Schedule H1 Report"
        modal={h1ReportModValues?.valueEntered}
        rightModal={!h1ReportModValues?.valueEntered}
        modalWidth={h1ReportModValues?.valueEntered ? 95 : 30}
      >
        <div className="scroll-90vh">
          {h1ReportModValues?.valueEntered ? (
            <div>
              <div className="flex1-end">
                <div className="mr20">
                  <CustomButton
                    text="⬇"
                    onClick={handleChangeReport}
                    smallBtn
                  />
                </div>
              </div>
              <CustomTable
                columns={tblHdrs}
                grey
                datas={getTableData()}
                page={page}
                rowsPerPage={itemSize}
                totalRecords={h1Records?.totalRecords}
                handleChangePage={handleChangePage}
                fullWidth
              />
            </div>
          ) : (
            <div className="ph20">
              <div className="txt-semi-bold fs14 light-grey">
                Stock Name <span className="red-color">*</span>
              </div>
              <CustomSelect
                list={getH1Stocks()}
                value={h1ReportModValues?.stockName ?? []}
                handleChange={(e) => {
                  onChange("stockName", e?.target?.value);
                }}
                multiSelectSearchCheck
                error={!!h1ReportModValues?.stockNameErr}
                helperText={h1ReportModValues?.stockNameErr}
              />
              <div className="flex-row-between mt10">
                <div className="w48Per">
                  <div className="txt-semi-bold fs14 light-grey">
                    Start Date
                  </div>
                  <CustomTextField
                    value={h1ReportModValues?.startDate}
                    fullWidth
                    mobileDate
                    handleChange={(e) =>
                      onChange("startDate", e?.target?.value)
                    }
                  />
                </div>
                <div className="w48Per">
                  <div className="txt-semi-bold fs14 light-grey">End Date</div>
                  <CustomTextField
                    value={h1ReportModValues?.endDate}
                    fullWidth
                    mobileDate
                    handleChange={(e) => onChange("endDate", e?.target?.value)}
                  />
                </div>
              </div>
              <div className="flex1-end mt20">
                <div>
                  <CustomButton text="Generate" onClick={onGenerate} smallBtn />
                </div>
              </div>
            </div>
          )}
        </div>
      </CustomModal>

      {periodReportModal()}
      {ndpsReportModal()}
      {dayBookReportModal()}
      {billsReportModal()}
      {soaReportModal()}

      <CustomModal
        open={delModVsble}
        onClose={handleCloseDelMod}
        header="Delete"
        modal
        modalWidth={30}
      >
        <Typography className="flex-center txt-regular fs14">
          Are you sure want to delete the payment?
        </Typography>

        <div className="flex-center mb10 mt15">
          <div>
            <CustomButton text="Cancel" onClick={handleCloseDelMod} smallBtn />
          </div>
          <div className="ml10">
            <CustomButton
              redBtn
              text="Confirm"
              onClick={handleConfirmDelete}
              smallBtn
            />
          </div>
        </div>
      </CustomModal>
    </>
  );
};

export default ClinicPayments;
